
import axios from 'axios';
import User from '../models/User';
import Session from "./Session";

/**
 * Auth class
 * @class
 */
class Auth
{
    /**
     * Api access token
     * @type {String}
     */
    private api_token: string;

    /**
     * Authenticated user
     * @type {User}
     */
    private authenticatable: User;

    /**
     * Promise that resolves once user is authenticated
     * @type {*}
     */
    private fetched: any;
    private resolveFetched: any;

    /**
     * Auth constructor
     * @constructor
     */
    constructor()
    {
        axios.interceptors.request.use( this.before.bind( this ) );

        this.fetched = new Promise( resolve => {
            this.resolveFetched = resolve;
        });


    }


    restore()
    {
        const token = Session.get( 'api_token' );
        if( token === null )
            return Promise.resolve();

        this.api_token = token;
        return User.fetch().then( this.authenticated.bind( this ), () => {
            this.api_token = null;
            Session.delete( 'api_token' );
        });
    }


    /**
     * Get authenticate promise
     * @return {Promise}
     */
    get authenticate(): any
    {
        return this.fetched;
    }


    /**
     * Returns true if user is authenticated, false otherwise.
     * @return {Boolean}
     */
    get check(): boolean
    {
        return this.api_token !== null && this.api_token !== undefined;
    }


    /**
     * Get api access token
     * @return {User}
     */
    get token(): string
    {
        return this.api_token;
    }


    /**
     * Get authenticated user
     * @return {User}
     */
    get user(): User
    {
        return this.authenticatable;
    }


    /**
     * Request interceptor
     * @param {Object} configuration
     * @return {Object}
     */
    before( configuration: any )
    {
        // append api token into outgoing requests
        if( this.check )
            configuration.params = { ...configuration.params, api_token: this.api_token };

        return configuration;
    }


    /**
     * Send password reset link to user
     * @param {String} email
     * @return {Promise<*>}
     */
    forgot( email: string ): Promise<any>
    {
        return axios.post( '/api/auth/forgot', { email } )
            .then( response => response.data, error => Promise.reject( error.response.data.errors ) );
    }


    /**
     * Authenticate user
     * @param {String} email
     * @param {String} password
     * @return {Promise<User>}
     */
    login( email: string, password: string ): Promise<User>
    {
        return axios.post( '/api/auth/login', { email, password } ).then( response => {
            this.api_token = response.data.api_token;
            localStorage.setItem( 'api_token', this.api_token );

            const user = new User( response.data.user, true );
            return this.authenticated( user );
        }, error => Promise.reject( error.response.data.errors ) );
    }


    /**
     * Logout user
     * @return {Promise}
     */
    logout()
    {
        if( this.user instanceof User === false )
            return Promise.reject();

        return axios.post( '/api/auth/logout' ).then( () => {
            this.api_token = null;
            this.authenticatable = null;

            localStorage.removeItem( 'api_token' );
        });
    }


    /**
     * Reset password
     * @param {String} token
     * @param {String} email
     * @param {String} password
     * @param {String} password_confirmation
     * @return {Promise<*>}
     */
    reset( token: string, email: string, password: string, password_confirmation: string ): Promise<any>
    {
        const attributes = { token, email, password, password_confirmation };

        return axios.post( '/api/auth/reset', attributes ).then( response => {
            return response.data;
        }, error => Promise.reject( error.response.data.errors ) );
    }


    /**
     * Authenticated event handler
     * @param {User} user
     * @return {Promise<User>}
     */
    private authenticated( user: User ): Promise<User>
    {
        this.authenticatable = user;

        this.resolveFetched( this.authenticatable );
        return Promise.resolve( this.authenticatable );
    }
}

export default new Auth;
